// FinalServicesSection.js
import React from 'react';
import { ArrowRight } from 'lucide-react';
import spinalCordCareImage from '../assets/spinal-cord-care-showcase.jpg';
import postOperativeCareImage from '../assets/post-operative-care-showcase.jpg';

const FinalServicesSection = () => {
  return (
    <section className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-20 md:space-y-28">

        {/* Spinal Cord Injury Care */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Image */}
          <div>
            <img
              src={spinalCordCareImage}
              alt="Carer with a woman in a wheelchair"
              className="w-full h-[420px] lg:h-[520px] rounded-2xl object-cover"
            />
          </div>

          {/* Right Content */}
          <div>
            <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
              Specialist Support
            </p>
            <h3 className="font-display font-light text-bnInk leading-[1.1] text-3xl md:text-4xl mb-6">
              Spinal Cord Injury Care
            </h3>

            <div className="space-y-4 text-bnBody text-base leading-relaxed mb-8">
              <p>
                Our spinal cord injury care is tailored to individuals living with partial or complete paralysis. The carers we match provide mobility assistance, daily living support, and safety monitoring to help clients maintain independence and quality of life at home. Each care plan is customised to meet the specific physical and emotional needs of the client.
              </p>

              <p>
                With experienced and compassionate carers, families can feel confident their loved ones are safe, supported, and empowered to live as fully as possible. From transfers and exercises to daily routines, our carers ensure that clients receive reliable care that promotes both safety and confidence.
              </p>

              <p className="text-bnInk font-medium">Our spinal cord injury care includes:</p>
            </div>

            <ul className="space-y-4">
              {['Mobility assistance', 'Daily support', 'Safety monitoring'].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border border-bnAccent text-bnAccent">
                    <ArrowRight size={14} />
                  </span>
                  <span className="text-bnInk font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Post-Operative Care */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div>
            <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
              Recovery Care
            </p>
            <h3 className="font-display font-light text-bnInk leading-[1.1] text-3xl md:text-4xl mb-6">
              Post-Operative Care
            </h3>

            <div className="space-y-4 text-bnBody text-base leading-relaxed mb-8">
              <p>
                Post-operative care helps seniors recover safely and comfortably after surgery or hospitalisation. The carers we refer assist with daily activities, medication reminders, wound care, and mobility, supporting a smooth and healthy recovery process.
              </p>

              <p>
                Families can trust that their loved ones are receiving professional, attentive care every step of the way. By providing personalised support and monitoring progress closely, our carers help clients regain strength, maintain independence, and return to their normal routines with confidence. We provide assistance with:
              </p>
            </div>

            <ul className="space-y-4">
              {['Wound care', 'Medication reminders', 'Mobility support'].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border border-bnAccent text-bnAccent">
                    <ArrowRight size={14} />
                  </span>
                  <span className="text-bnInk font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right Image */}
          <div>
            <img
              src={postOperativeCareImage}
              alt="Carer assisting an older woman"
              className="w-full h-[420px] lg:h-[520px] rounded-2xl object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalServicesSection;
